import { Dialog, DialogContent } from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { LegacyDocumentType } from '@prisma/client';
import { useRef, useState } from 'react';
import { TUAEIncorporationRequestData } from '../../../types';
import { IndividualDirectorFormSteps } from './constants';
import { DocumentInfoForm } from './DocumentInfo';
import { PassportInfoForm } from './PassportInfo';
import { PersonalInfoForm } from './PersonalDetails';
import {
  TDocumentInfoFields,
  TPassportInfoFields,
  TPersonalDetailFields,
} from './schema';

type TIndividualDirectorFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
  setNotification: TSetNotification;
  director?: DirectorBase | null;
  onSuccess?: (director: DirectorBase) => void;
};

type TIndividualDirectorFormData = {
  personalInfo: TPersonalDetailFields | null;
  passportInfo: TPassportInfoFields | null;
  documentInfo: TDocumentInfoFields | null;
};

export const IndividualDirectorFormModal = ({
  isOpen,
  onClose,
  workRequest,
  setNotification,
  onSuccess,
  director,
}: TIndividualDirectorFormModalProps) => {
  const [step, setStep] = useState(
    IndividualDirectorFormSteps.PERSONAL_DETAILS
  );

  console.log({ director });

  const individualFormDataRef = useRef<TIndividualDirectorFormData>({
    personalInfo: {
      directorFirstName: director?.directorFirstName || '',
      directorLastName: director?.directorLastName || '',
      email: director?.email || '',
      number: director?.number || '',
      operatingAddress: {
        addressLine1: director?.operatingAddress?.addressLine1 || '',
        addressLine2: director?.operatingAddress?.addressLine2 || '',
        postalCode: director?.operatingAddress?.postalCode || '',
        city: director?.operatingAddress?.city || '',
        state: director?.operatingAddress?.state || '',
        country: director?.operatingAddress?.country || '',
      },
      personalDetails: {
        isPoliticallyExposed:
          director?.personalDetails?.isPoliticallyExposed || false,
        nationality: director?.personalDetails?.nationality || '',
      },
    },
    passportInfo: {
      personalDetails: {
        haveUAEResidenceVisa:
          director?.personalDetails?.haveUAEResidenceVisa || true,
        passportCountryOfIssue:
          director?.personalDetails?.passportCountryOfIssue || '',
        passportExpirationDate:
          director?.personalDetails?.passportExpirationDate || '',
        passportNumber: director?.personalDetails?.passportNumber || '',
      },
    },
    documentInfo: null,
  });

  const generatePayload = () => {
    const individualFormData = individualFormDataRef.current;
    const personalDetails = individualFormData.personalInfo
      ?.personalDetails as {
      nationality: string;
      isPoliticallyExposed: boolean;
    };

    const workRequestId = workRequest?.id;

    return {
      directorFirstName: individualFormData.personalInfo.directorFirstName,
      directorLastName: individualFormData.personalInfo.directorLastName,
      number: individualFormData.personalInfo.number,
      email: individualFormData.personalInfo.email,
      personalDetails: {
        nationality: personalDetails?.nationality,
        isPoliticallyExposed: personalDetails?.isPoliticallyExposed,
        haveUAEResidenceVisa:
          individualFormData.passportInfo.personalDetails.haveUAEResidenceVisa,
        passportCountryOfIssue:
          individualFormData.passportInfo.personalDetails
            .passportCountryOfIssue,
        passportExpirationDate:
          individualFormData.passportInfo.personalDetails
            .passportExpirationDate,
        passportNumber:
          individualFormData.passportInfo.personalDetails.passportNumber,
      },
      companyId: workRequest.companyId,
      operatingAddress: individualFormData.personalInfo.operatingAddress,
      corporationHasDirector: [
        {
          corporationId: workRequest.corporationId,
        },
      ],
      addressProof: {
        ...individualFormData.documentInfo.addressProof,
        documentType: LegacyDocumentType.AddressProof,
      },
      filesToAdd: [
        {
          ...individualFormData.passportInfo.passport,
          documentType: LegacyDocumentType.Passport,
        },
        {
          ...individualFormData.documentInfo.emiratesId,
          documentType: LegacyDocumentType.EmiratesId,
        },
        {
          ...individualFormData.documentInfo.residenceVisa,
          documentType: LegacyDocumentType.Visa,
        },
      ],
      ...(workRequestId != null ? { wrkReqId: workRequestId } : {}),
    };
  };

  const onSubmit = async () => {
    const payload = generatePayload();
    const res = await fetcher(
      director ? `director/${director.id}` : 'director',
      {
        method: 'POST',
        body: payload,
      },
      undefined
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again later or contact team Commenda if you need help',
      });
    }
    const responseData = await res.json();
    if (onSuccess) onSuccess(responseData);
    onClose();
  };

  let jsx;
  switch (step) {
    case IndividualDirectorFormSteps.PERSONAL_DETAILS:
      jsx = (
        <PersonalInfoForm
          onNext={(formData) => {
            individualFormDataRef.current.personalInfo = formData;
            setStep(IndividualDirectorFormSteps.PASSPORT_INFO);
          }}
          onBack={onClose}
          director={director}
          defaultValues={individualFormDataRef.current.personalInfo}
        />
      );
      break;
    case IndividualDirectorFormSteps.PASSPORT_INFO:
      jsx = (
        <PassportInfoForm
          companyId={workRequest.companyId}
          onNext={(formData) => {
            individualFormDataRef.current.passportInfo = formData;
            setStep(IndividualDirectorFormSteps.DOCUMENTS);
          }}
          onBack={() => setStep(IndividualDirectorFormSteps.PERSONAL_DETAILS)}
          director={director}
          defaultValues={individualFormDataRef.current.passportInfo}
        />
      );
      break;
    case IndividualDirectorFormSteps.DOCUMENTS:
      jsx = (
        <DocumentInfoForm
          companyId={workRequest.companyId}
          onNext={(formData) => {
            individualFormDataRef.current.documentInfo = formData;
            onSubmit();
          }}
          onBack={() => setStep(IndividualDirectorFormSteps.PASSPORT_INFO)}
          director={director}
        />
      );
      break;
    default:
      jsx = null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>{jsx}</DialogContent>
    </Dialog>
  );
};
